import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface Partner {
  id: string;
  name: string;
  billing_details: any;
}

interface Invoice {
  id: string;
  partner_id: string;
  invoice_number?: number;
  partners: { 
    name: string;
    billing_details: {
      tax_id: string;
      legal_name: string;
      address: string;
    }
  };
  period_month: number;
  period_year: number;
  total_amount: number;
  status: 'draft' | 'sent' | 'paid';
  created_at: string;
}

export default function AdminInvoices() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genData, setGenData] = useState({
    partner_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    invoiceNumber: ''
  });
  const [previewTotal, setPreviewTotal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const { data: pData } = await supabase.from('partners').select('id, name, billing_details');
    const { data: iData } = await supabase.from('partner_invoices').select('*, partners(name, billing_details)').order('created_at', { ascending: false });
    
    if (pData) setPartners(pData);
    if (iData) setInvoices(iData);
    setLoading(false);
  };

  const calculatePreview = async () => {
    if (!genData.partner_id) return;
    setError(null);

    // Fetch completed (not yet invoiced) alterations for this partner
    const { data } = await supabase
      .from('alterations')
      .select('price')
      .eq('partner_id', genData.partner_id)
      .eq('status', 'completed');
    
    if (data && data.length > 0) {
      const total = data.reduce((sum, item) => sum + Number(item.price || 0), 0);
      setPreviewTotal(total);
    } else {
      setPreviewTotal(0);
      setError("No hay arreglos completados pendientes de facturar.");
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    await supabase.from('partner_invoices').update({ status: newStatus }).eq('id', id);
    fetchData();
  };

  const handleGenerate = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    await createInvoice(previewTotal || 0);
    setIsSubmitting(false);
  };

  const createInvoice = async (amount: number) => {
    const { data: maxInv } = await supabase
      .from('partner_invoices')
      .select('invoice_number')
      .order('invoice_number', { ascending: false })
      .limit(1)
      .maybeSingle();
      
    const manualNumber = genData.invoiceNumber ? Number(genData.invoiceNumber) : null;
    const nextInvoiceNumber = manualNumber || (maxInv && maxInv.invoice_number ? maxInv.invoice_number + 1 : 1);

    const { data: inv, error: invError } = await supabase.from('partner_invoices').insert([{
      partner_id: genData.partner_id,
      period_month: genData.month,
      period_year: genData.year,
      total_amount: amount,
      status: 'draft',
      invoice_number: nextInvoiceNumber
    }]).select().single();

    if (invError) { setError("Error DB: " + invError.message); return; }

    if (inv) {
      await supabase.from('alterations').update({ status: 'invoiced', invoice_id: inv.id }).eq('partner_id', genData.partner_id).eq('status', 'completed');
      setIsGenerating(false);
      fetchData();
    }
  };

  const handleDelete = async (id: string) => {
    // Borrado directo sin alertas de navegador
    setLoading(true);
    try {
      // 1. Liberamos los arreglos
      await supabase.from('alterations').update({ status: 'completed', invoice_id: null }).eq('invoice_id', id);
      // 2. Borramos la factura
      const { error } = await supabase.from('partner_invoices').delete().eq('id', id);
      if (error) throw error;
      fetchData();
    } catch (err: any) {
      setError("Error al eliminar: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-brand-black text-2xl">Facturación B2B</h2>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">Gestión de cobros y facturas mensuales.</p>
        </div>
        <button
          onClick={() => { setIsGenerating(true); setError(null); setPreviewTotal(null); setGenData({ partner_id: '', month: new Date().getMonth() + 1, year: new Date().getFullYear(), invoiceNumber: '' }); }}
          className="bg-brand-black text-white px-5 py-2.5 rounded-full text-sm font-sans flex items-center gap-2"
        >
          <i className="ri-file-add-line" /> Nueva Factura
        </button>
      </div>

      {error && !isGenerating && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm font-sans flex items-center justify-between border border-red-100">
          <div className="flex items-center gap-3">
            <i className="ri-error-warning-line" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><i className="ri-close-line" /></button>
        </div>
      )}

      {isGenerating && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-brand-cream/30 p-8 rounded-3xl border border-brand-sage/30 max-w-2xl mx-auto shadow-xl">
          <h3 className="font-serif text-xl mb-6">Generar Factura</h3>
          {error && <div className="mb-6 p-4 rounded-xl text-sm bg-red-50 text-red-600">{error}</div>}
          <div className="grid grid-cols-2 gap-6">
            <select className="w-full bg-white border rounded-xl px-4 py-2.5 text-sm" value={genData.partner_id} onChange={(e) => setGenData({ ...genData, partner_id: e.target.value })}>
              <option value="">Marca Partner</option>
              {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="flex gap-2">
              <select className="flex-1 bg-white border rounded-xl px-2 py-2.5 text-sm" value={genData.month} onChange={(e) => setGenData({ ...genData, month: Number(e.target.value) })}>
                {Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('es', { month: 'short' })}</option>)}
              </select>
              <select className="flex-1 bg-white border rounded-xl px-2 py-2.5 text-sm" value={genData.year} onChange={(e) => setGenData({ ...genData, year: Number(e.target.value) })}>
                <option value={2026}>2026</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="block font-sans text-[10px] uppercase tracking-widest text-brand-gray-400 font-bold mb-1.5">Nº Factura (opcional)</label>
            <input
              type="number"
              placeholder="Automático"
              className="w-40 bg-white border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-sage"
              value={genData.invoiceNumber}
              onChange={(e) => setGenData({ ...genData, invoiceNumber: e.target.value })}
            />
            <p className="text-[10px] text-brand-gray-400 mt-1">Déjalo vacío para asignar el número siguiente automáticamente.</p>
          </div>
          <div className="mt-8 flex items-center justify-between">
            <div>
              <p className="font-sans text-[10px] uppercase tracking-widest text-brand-gray-400 font-bold mb-1">Base Imponible: {previewTotal?.toFixed(2)} €</p>
              <p className="font-serif text-2xl">{previewTotal !== null ? (previewTotal * 1.21).toFixed(2) : '0.00'} €</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setIsGenerating(false)} className="px-6 py-2.5 text-sm text-brand-gray-500">Cancelar</button>
              {previewTotal === null
                ? <button onClick={calculatePreview} className="bg-brand-black text-white px-8 py-2.5 rounded-full text-sm">Calcular</button>
                : <button onClick={() => handleGenerate()} disabled={!previewTotal || previewTotal <= 0 || isSubmitting} className="bg-brand-sage px-8 py-2.5 rounded-full text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                    {isSubmitting && <i className="ri-loader-4-line animate-spin" />}
                    Generar
                  </button>
              }
            </div>
          </div>
        </motion.div>
      )}

      <div className="bg-white rounded-3xl border border-brand-gray-100 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-brand-gray-50 border-b">
              <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Nº Factura</th>
              <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Marca / Razón Social</th>
              <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Periodo</th>
              <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Total</th>
              <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Estado</th>
              <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {invoices.map((inv) => (
              <tr key={inv.id} className="hover:bg-brand-gray-50/50 transition-colors">
                <td className="px-6 py-4">
                  <span className="font-mono font-bold text-brand-black text-sm">
                    #{inv.invoice_number ? String(inv.invoice_number).padStart(4, '0') : '—'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <p className="font-sans font-medium text-sm text-brand-black">{inv.partners?.name}</p>
                  <p className="text-[10px] text-brand-gray-400">{inv.partners?.billing_details?.tax_id}</p>
                </td>
                <td className="px-6 py-4 font-sans text-sm capitalize">{new Date(0, inv.period_month - 1).toLocaleString('es', { month: 'long' })} {inv.period_year}</td>
                <td className="px-6 py-4 font-serif font-bold text-brand-black">{(inv.total_amount * 1.21).toFixed(2)} €</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-[9px] uppercase tracking-widest font-bold ${inv.status === 'paid' ? 'bg-brand-sage/10 text-brand-sage' : 'bg-amber-50 text-amber-600'}`}>
                    {inv.status === 'paid' ? 'Pagada' : 'Pendiente'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {inv.status !== 'paid' && (
                      <button 
                        onClick={() => handleStatusChange(inv.id, 'paid')}
                        className="p-2 hover:bg-brand-sage/10 rounded-full text-brand-gray-400 hover:text-brand-sage transition-colors" 
                        title="Marcar como Pagada"
                      >
                        <i className="ri-bank-card-line" />
                      </button>
                    )}
                    <Link to={`/invoice/${inv.id}`} className="p-2 hover:bg-brand-gray-100 rounded-full text-brand-gray-400 hover:text-brand-black transition-colors" title="Ver Factura">
                      <i className="ri-external-link-line" />
                    </Link>
                    <button 
                      onClick={() => handleDelete(inv.id)} 
                      className="p-2 hover:bg-red-50 rounded-full text-brand-gray-400 hover:text-red-600 transition-colors"
                      title="Eliminar Factura"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
